import type { CSSProperties, SVGProps } from "react";

import { PlaceholderDarkSymbol, PlaceholderLightSymbol } from "~/assets";
import type { AssetLogoTheme } from "~/v2/hooks/useAssetLogoTheme";
import { cn } from "~/v2/utils/cn";
import type { AssetIconSize } from "../AssetSearch.types";
import type { AssetIconVisualState } from "./assetIcon.derive";

export type { AssetIconVisualState };

/**
 * Visually-hidden style used by the preload `<img>` below.
 *
 * The preload trick fires a real network request for the asset logo before it
 * is swapped into the presenter's SVG `<image>`. It must:
 *   1. remain in the DOM (so the browser downloads it — `display: none` on the
 *      element itself is respected by browsers and short-circuits the fetch);
 *   2. take zero visual/layout space and be inert to the pointer/AT tree
 *      (`aria-hidden="true"` on the element handles the AT tree separately).
 *
 * The Tailwind `sr-only` utility was previously used for this; it is not
 * allowed in the Radix Themes 3.3 target, so the equivalent rule is inlined
 * here. Radix Themes has no preloader primitive, so this stays as a raw img
 * with an inline style rather than being expressed as a Radix component.
 */
const PRELOAD_IMG_STYLE: CSSProperties = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

// ---------------------------------------------------------------------------
// Size mapping — SVG width/height attributes in pixels, no Tailwind classes.
// ---------------------------------------------------------------------------

const SIZE_PX: Record<AssetIconSize, number> = {
  sm: 16,
  base: 20,
  lg: 26,
  xl: 32,
};

/** Font size in SVG viewBox units (0 0 32 32) for each icon size. */
const TEXT_SIZE: Record<AssetIconSize, number> = {
  sm: 9,
  base: 9,
  lg: 10,
  xl: 13,
};

export interface AssetIconPresentationProps
  extends Omit<SVGProps<SVGSVGElement>, "name"> {
  visualState: AssetIconVisualState;
  initials: string;
  theme: AssetLogoTheme;
  size?: AssetIconSize;
  /** Confirmed-loaded image URL; only used in remote state. */
  imageSrc?: string;
  imageAlt?: string;
  preloadSrc?: string;
  onPreloadLoad: () => void;
  onPreloadError: () => void;
}

/**
 * Pure hook-free presentation for AssetIcon.
 *
 * Always renders one root <svg> forwarding aria-*, role, id, style, data-*,
 * event handlers, tabIndex, and className to that root across all visual states.
 *
 * Visual states:
 * - "loading"  → skeleton rect with an SVG-native opacity pulse (no Tailwind
 *                `animate-pulse` class; the animation is expressed via a SMIL
 *                `<animate>` element so the presenter carries no CSS token
 *                names from the old atd layer).
 * - "initials" → PlaceholderLight/DarkSymbol from ~/assets as a nested SVG
 *                (aria-hidden) + two-char initials text element in root
 *                coordinate space.
 * - "remote"   → SVG <image> for the confirmed-loaded logo.
 *
 * Colour tokens come from Radix semantic variables — `--gray-a5` for the
 * skeleton fill and `--gray-11` for the initials fill — so the icon tracks
 * the appearance without a bespoke ATD scale.
 */
export function AssetIconPresentation({
  visualState,
  initials,
  theme,
  size = "base",
  imageSrc,
  imageAlt,
  preloadSrc,
  onPreloadLoad,
  onPreloadError,
  style,
  className,
  ...svgProps
}: AssetIconPresentationProps) {
  const px = SIZE_PX[size];
  const fontSize = TEXT_SIZE[size];
  const PlaceholderSymbol =
    theme === "dark" ? PlaceholderDarkSymbol : PlaceholderLightSymbol;

  // Callers may still pass a runtime `style` (e.g. opacity); shrink-0 is
  // static layout and lives on className so the icon never squeezes in a
  // flex row.
  const rootStyle: CSSProperties | undefined = style;

  return (
    <>
      <svg
        viewBox="0 0 32 32"
        xmlns="http://www.w3.org/2000/svg"
        width={px}
        height={px}
        aria-hidden="true"
        className={cn("shrink-0", className)}
        style={rootStyle}
        {...svgProps}
      >
        {visualState === "loading" && (
          <rect x="0" y="0" width="32" height="32" rx="4" fill="var(--gray-a5)">
            {/* SMIL pulse — no CSS keyframes, no Tailwind class. Runs
              indefinitely at ~1s intervals, matching the perceptual rhythm
              of the previous `animate-pulse` class. */}
            <animate
              attributeName="opacity"
              values="0.4;1;0.4"
              dur="1.6s"
              repeatCount="indefinite"
            />
          </rect>
        )}

        {visualState === "initials" && (
          <>
            {/* Nested placeholder SVG from ~/assets; fills the root viewport. */}
            <PlaceholderSymbol width="32" height="32" aria-hidden="true" />
            <text
              x="16"
              y="16"
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={fontSize}
              fontWeight="600"
              fill="var(--gray-11)"
              className="pointer-events-none select-none"
            >
              {initials}
            </text>
          </>
        )}

        {visualState === "remote" && imageSrc && (
          <image
            href={imageSrc}
            x="0"
            y="0"
            width="32"
            height="32"
            aria-label={imageAlt}
          />
        )}
      </svg>
      {preloadSrc !== undefined && (
        <img
          src={preloadSrc}
          alt=""
          aria-hidden="true"
          style={PRELOAD_IMG_STYLE}
          onLoad={onPreloadLoad}
          onError={onPreloadError}
        />
      )}
    </>
  );
}
